import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './section-header.lit.scss';
import getString from './strings';

/**
 * @component {kat-section-header} KatSectionHeader The Section Header divides a larger page into smaller, more manageable sections. This may be done as a part of an Entity Detail page or a Workflow.
 * @slot default Optional section content.
 * @example Default {"content": "<p>Section Content</p>", "header": "Section Header", "status": "Status", "action-label": "Action", "action-icon": "info", "expandable": true}
 * @example NotCollapsible {"content": "<p>Section cannot be collapsed</p>", "header": "Action Icon Only", "status": "Error", "status-type": "danger", "action-icon": "gear" }
 * @example StartingCollapsed {"content": "<p>Section starts collapsed</p>", "header": "Action Label Only", "status": "Success", "status-type": "success", "expandable": true, "collapsed": true }
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-section-header')
export class KatSectionHeader extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /** Header that will be displayed. */
  @property()
  header?: string;

  /** Status that will be displayed. */
  @property()
  status?: string;

  /** Status type used when `status` is set.  Default is `default`.
   * @enum {value} default Used for a generic status or state
   * @enum {value} success Items that have positive meaning
   * @enum {value} warning Items that have negative meaning
   * @enum {value} danger Items with problems or that have been removed
   * @enum {value} info Items that are informational
   * @enum {value} tag Tag-style badges are used when displaying neutral values or messaging.
   */
  @property({ attribute: 'status-type' })
  statusType? = 'default';

  /** Label to use for the action-button. Reduces to `action-icon` at smaller breakpoints if provided. */
  @property({ attribute: 'action-label' })
  actionLabel?: string;

  /** Icon to use for the action-button.  See `kat-icon` for all options. */
  @property({ attribute: 'action-icon' })
  actionIcon?: string;

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property()
  locale?: string;

  /** Whether the section is able to be expanded.  Default is false. */
  @property()
  expandable?: boolean;

  /** Whether the content provided is expanded.  Default is true when expandable is true. */
  @property()
  collapsed?: boolean;

  /** Triggers when the action button is clicked. */
  @event('action', true)
  private _actionEvent: EventEmitter;

  /** Broadcasts when the section is expanded. */
  @event('expand')
  private _expand: EventEmitter;

  /** Broadcasts when the section is collapsed. */
  @event('collapse')
  private _collapse: EventEmitter;

  private _toggle() {
    this.collapsed = !this.collapsed;
    (this.collapsed ? this._collapse : this._expand).emit();
  }

  private _handleActionClick(e: MouseEvent) {
    e.stopPropagation();
    this._actionEvent.emit();
  }

  private _handleSectionClick() {
    if (this.expandable) {
      this._toggle();
    }
  }

  private _handleToggleClick(e: MouseEvent) {
    e.stopPropagation();
    this._toggle();
  }

  private _getRenderedHeader() {
    if (!this.header) {
      return nothing;
    }

    return html`<h3>${this.header}</h3>`;
  }

  private _getRenderedStatus() {
    if (!this.status) {
      return nothing;
    }

    return html`<kat-badge type=${this.statusType}>${this.status}</kat-badge>`;
  }

  private _getRenderedAction() {
    const renderedActionIcon = this.actionIcon
      ? html`<kat-icon
          name=${this.actionIcon}
          size="tiny"
          class="action-icon"
        ></kat-icon>`
      : nothing;

    const renderedActionLabel = this.actionLabel
      ? html`<span class="action-label">${this.actionLabel}</span>`
      : nothing;

    if (!this.actionLabel && !this.actionIcon) {
      return nothing;
    }

    return html`<kat-button
      variant="utility"
      id="action"
      @click=${this._handleActionClick}
      >${renderedActionIcon}${renderedActionLabel}</kat-button
    >`;
  }

  private _getRenderedToggle() {
    if (!this.expandable) {
      return nothing;
    }

    return html`<kat-button
      variant="utility-link"
      id="toggle"
      aria-label=${getString(
        'kat-section-header-toggle',
        undefined,
        this.locale
      )}
      part="section-header-toggle"
      @click=${this._handleToggleClick}
      ><kat-icon
        name=${this.collapsed ? 'expand_more' : 'expand_less'}
        size="tiny"
      ></kat-icon
    ></kat-button>`;
  }

  render() {
    return html`<div class="header" @click=${this._handleSectionClick}>
        <div class="header-content">
          ${this._getRenderedHeader()}${this._getRenderedStatus()}
        </div>
        <div class="actions">
          ${this._getRenderedAction()}${this._getRenderedToggle()}
        </div>
      </div>
      <div class="content" part="section-header-content"><slot></slot></div>`;
  }
}
